// FireCheck Offline Database using IndexedDB
const DB_NAME = 'firecheck-offline';
const DB_VERSION = 1;

const STORES = {
  inspections: 'inspections',
  checklists: 'checklists',
  pendingSync: 'pendingSync',
  photos: 'photos'
};

let db = null;

// Initialize IndexedDB
export async function initDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // Inspections store
      if (!database.objectStoreNames.contains(STORES.inspections)) {
        const inspStore = database.createObjectStore(STORES.inspections, { keyPath: 'id' });
        inspStore.createIndex('status', 'status', { unique: false });
        inspStore.createIndex('syncStatus', 'syncStatus', { unique: false });
      }

      // Checklists store
      if (!database.objectStoreNames.contains(STORES.checklists)) {
        database.createObjectStore(STORES.checklists, { keyPath: 'id' });
      }

      // Pending sync queue
      if (!database.objectStoreNames.contains(STORES.pendingSync)) {
        const syncStore = database.createObjectStore(STORES.pendingSync, { keyPath: 'id', autoIncrement: true });
        syncStore.createIndex('timestamp', 'timestamp', { unique: false });
        syncStore.createIndex('type', 'type', { unique: false });
      }

      // Photos store (for offline photo storage)
      if (!database.objectStoreNames.contains(STORES.photos)) {
        const photoStore = database.createObjectStore(STORES.photos, { keyPath: 'id' });
        photoStore.createIndex('inspectionId', 'inspectionId', { unique: false });
      }
    };
  });
}

// Get database instance
async function getDB() {
  if (!db) {
    await initDB();
  }
  return db;
}

// ==========================================
// INSPECTIONS
// ==========================================

export async function saveInspectionOffline(inspection) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.inspections], 'readwrite');
    const store = transaction.objectStore(STORES.inspections);
    
    // Mark with sync status
    const offlineInspection = {
      ...inspection,
      syncStatus: inspection.syncStatus || 'synced',
      lastModified: new Date().toISOString()
    };
    
    const request = store.put(offlineInspection);
    request.onsuccess = () => resolve(offlineInspection);
    request.onerror = () => reject(request.error);
  });
}

export async function getInspectionOffline(id) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.inspections], 'readonly');
    const store = transaction.objectStore(STORES.inspections);
    const request = store.get(id);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getAllInspectionsOffline() {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.inspections], 'readonly');
    const store = transaction.objectStore(STORES.inspections);
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

export async function deleteInspectionOffline(id) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.inspections], 'readwrite');
    const store = transaction.objectStore(STORES.inspections);
    const request = store.delete(id);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

// ==========================================
// CHECKLISTS
// ==========================================

export async function saveChecklistOffline(checklist) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.checklists], 'readwrite');
    const store = transaction.objectStore(STORES.checklists);
    const request = store.put(checklist);
    request.onsuccess = () => resolve(checklist);
    request.onerror = () => reject(request.error);
  });
}

export async function getChecklistOffline(id) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.checklists], 'readonly');
    const store = transaction.objectStore(STORES.checklists);
    const request = store.get(id);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getAllChecklistsOffline() {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.checklists], 'readonly');
    const store = transaction.objectStore(STORES.checklists);
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

// ==========================================
// PENDING SYNC QUEUE
// ==========================================

export async function addToPendingSync(action) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.pendingSync], 'readwrite');
    const store = transaction.objectStore(STORES.pendingSync);
    
    const syncItem = {
      ...action,
      timestamp: new Date().toISOString(),
      retryCount: 0
    };
    
    const request = store.add(syncItem);
    request.onsuccess = () => resolve({ ...syncItem, id: request.result });
    request.onerror = () => reject(request.error);
  });
}

export async function getPendingSyncItems() {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.pendingSync], 'readonly');
    const store = transaction.objectStore(STORES.pendingSync);
    const index = store.index('timestamp');
    const request = index.getAll();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

export async function removePendingSyncItem(id) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.pendingSync], 'readwrite');
    const store = transaction.objectStore(STORES.pendingSync);
    const request = store.delete(id);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function clearPendingSync() {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.pendingSync], 'readwrite');
    const store = transaction.objectStore(STORES.pendingSync);
    const request = store.clear();
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

// ==========================================
// PHOTOS
// ==========================================

export async function savePhotoOffline(photo) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.photos], 'readwrite');
    const store = transaction.objectStore(STORES.photos);
    const request = store.put(photo);
    request.onsuccess = () => resolve(photo);
    request.onerror = () => reject(request.error);
  });
}

export async function getPhotosForInspection(inspectionId) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.photos], 'readonly');
    const store = transaction.objectStore(STORES.photos);
    const index = store.index('inspectionId');
    const request = index.getAll(inspectionId);
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

export async function deletePhotoOffline(id) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.photos], 'readwrite');
    const store = transaction.objectStore(STORES.photos);
    const request = store.delete(id);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

// ==========================================
// UTILITY
// ==========================================

export async function clearAllOfflineData() {
  const database = await getDB();
  const transaction = database.transaction(Object.values(STORES), 'readwrite');
  
  return Promise.all(
    Object.values(STORES).map(storeName => {
      return new Promise((resolve, reject) => {
        const store = transaction.objectStore(storeName);
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    })
  );
}

export async function getOfflineStats() {
  const inspections = await getAllInspectionsOffline();
  const pendingItems = await getPendingSyncItems();
  
  return {
    totalInspections: inspections.length,
    pendingSync: pendingItems.length,
    unsyncedInspections: inspections.filter(i => i.syncStatus === 'pending').length
  };
}
